import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Lightbulb,
  ShieldCheck,
  AlertTriangle,
  FolderOpen,
  FileText,
  MessageSquare,
  Code,
  Clock,
  Download,
  ExternalLink,
  ChevronRight,
  BarChart3,
  Loader2,
  Trash2,
  MapPin,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { buildFullExportData } from "@/components/ruby-ai-v2/reports/exportDataUtils";
import RubyExportDialog from "@/components/ruby-ai-v2/reports/RubyExportDialog";
import { loadArtifacts, deleteArtifact, type Artifact } from "@/components/ruby-ai-v2/services/artifactService";

// ── Card definitions ──

const CARDS = [
  { id: "search-intelligence", label: "Search Intelligence", icon: Search, color: "#8B5CF6", route: "/ruby-aissist/search-intelligence" },
  { id: "brand-insight", label: "Brand Intelligence", icon: Lightbulb, color: "#FF7A18", route: "/ruby-aissist/brand-insight" },
  { id: "ai-readiness", label: "AI Readiness", icon: ShieldCheck, color: "#3B82F6", route: "/ruby-aissist/ai-readiness" },
  { id: "ai-vulnerability", label: "AI Risk & Exposure", icon: AlertTriangle, color: "#EF4444", route: "/ruby-aissist/ai-vulnerability" },
  { id: "local-intelligence", label: "Local Intelligence", icon: MapPin, color: "#22C55E", route: "/ruby-aissist/local-intelligence" },
] as const;

type CardId = (typeof CARDS)[number]["id"];

interface AuditRecord {
  id: string;
  domain: string;
  card_id: string;
  shortcut_id: string;
  edge_function?: string;
  region: string;
  structured_data?: any;
  analysis_text?: string;
  recommendations?: string[];
  processing_time?: number;
  created_at: string;
}

interface ChatRecord {
  id: string;
  card_id: string;
  tab_id: string;
  domain: string | null;
  messages: any[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

type ViewMode = "cards" | "folder" | "detail";
type SubTab = "audits" | "chats" | "artifacts";

function formatTimeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
}

function shortcutLabel(id: string): string {
  const map: Record<string, string> = {
    "authority44-import": "OG01.ai Import",
    "full-seo-audit": "Full SEO Audit", "keyword-rankings": "Keyword Rankings", "keyword-discovery": "Keyword Discovery",
    "competitor-analysis": "Competitor Analysis", "technical-seo": "Technical SEO", "content-gaps": "Content Gaps",
    "backlinks": "Backlinks", "traffic-trends": "Traffic & Trends", "serp-analysis": "SERP Analysis",
    "page-level-audit": "Page-Level Audit", "domain-pages": "Domain Pages", "authority-trust": "Authority & Trust",
    "ai-maturity": "AI Maturity", "ai-visibility": "AI Visibility", "ai-citation": "AI Citation",
    "ai-engine-comparison": "AI Engine Comparison", "content-alignment": "Content Alignment",
    "competitor-ai": "Competitor AI", "aeo-strategy": "AEO Strategy", "ai-prompt-tracking": "AI Prompt Tracking",
    "brand-audit": "Brand Audit", "visual-identity": "Visual Identity", "voice-personality": "Voice & Personality",
    "authority-analysis": "Authority Analysis", "audience-insights": "Audience Insights",
    "competitor-benchmarking": "Competitor Benchmarking", "brand-ai-presence": "Brand AI Presence",
    "brand-backlink-profile": "Brand Backlink Profile",
  };
  return map[id] || id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const ResourceVaultV2: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const userId = user?.id;

  const [viewMode, setViewMode] = useState<ViewMode>("cards");
  const [activeCard, setActiveCard] = useState<CardId | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("audits");
  const [selectedAudit, setSelectedAudit] = useState<AuditRecord | null>(null);

  const [audits, setAudits] = useState<AuditRecord[]>([]);
  const [chats, setChats] = useState<ChatRecord[]>([]);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [cardCounts, setCardCounts] = useState<Record<string, { audits: number; chats: number; artifacts: number }>>({});

  const [exportDialog, setExportDialog] = useState<{ open: boolean; title: string; data: any[]; auditResult?: any; domain?: string }>({ open: false, title: "", data: [] });

  // Handle URL params for pre-filtering (e.g., ?card=search-intelligence)
  useEffect(() => {
    const cardParam = searchParams.get("card");
    if (cardParam && CARDS.some((c) => c.id === cardParam)) {
      setActiveCard(cardParam as CardId);
      setViewMode("folder");
      loadCardData(cardParam as CardId);
    } else {
      loadOverviewCounts();
    }
  }, [userId]);

  const loadOverviewCounts = async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        CARDS.map(async (card) => {
          const [auditRes, artifactRes, chatRes] = await Promise.all([
            supabase.from("card_audit_results").select("*", { count: "exact", head: true })
              .eq("user_id", userId).eq("card_id", card.id)
              .not("shortcut_id", "like", "artifact-%"),
            supabase.from("card_audit_results").select("*", { count: "exact", head: true })
              .eq("user_id", userId).eq("card_id", card.id)
              .like("shortcut_id", "artifact-%"),
            supabase.from("chat_history").select("*", { count: "exact", head: true })
              .eq("user_id", userId).eq("card_id", card.id),
          ]);
          return [card.id, {
            audits: auditRes.count ?? 0,
            artifacts: artifactRes.count ?? 0,
            chats: chatRes.count ?? 0,
          }] as const;
        })
      );
      setCardCounts(Object.fromEntries(results));
    } catch (err) {
      console.warn("[Vault] Failed to load counts:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadCardData = async (cardId: CardId) => {
    if (!userId) return;
    setLoading(true);
    try {
      const [auditRes, chatRes, artifactData] = await Promise.all([
        supabase.from("card_audit_results")
          .select("id, domain, card_id, shortcut_id, region, created_at")
          .eq("user_id", userId).eq("card_id", cardId)
          .order("created_at", { ascending: false }).limit(50),
        supabase.from("chat_history")
          .select("id, card_id, tab_id, domain, is_active, updated_at, created_at, messages:messages")
          .eq("user_id", userId).eq("card_id", cardId)
          .order("updated_at", { ascending: false }).limit(50),
        loadArtifacts({ userId, cardId }),
      ]);
      setAudits((auditRes.data || []) as AuditRecord[]);
      setChats((chatRes.data || []) as ChatRecord[]);
      setArtifacts(artifactData);
    } catch (err) {
      console.warn("[Vault] Failed to load card data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (cardId: CardId) => {
    setActiveCard(cardId);
    setViewMode("folder");
    setActiveSubTab("audits");
    setSelectedAudit(null);
    setSearchParams({ card: cardId });
    loadCardData(cardId);
  };

  const handleBack = () => {
    if (viewMode === "detail") {
      setViewMode("folder");
      setSelectedAudit(null);
    } else {
      setViewMode("cards");
      setActiveCard(null);
      setSelectedAudit(null);
      setSearchParams({});
      loadOverviewCounts();
    }
  };

  const handleAuditClick = async (audit: AuditRecord) => {
    setSelectedAudit(audit);
    setViewMode("detail");
    const { data } = await supabase.from("card_audit_results")
      .select("*")
      .eq("id", audit.id)
      .single();
    if (data) setSelectedAudit(data as AuditRecord);
  };

  const handleLoadToDashboard = (audit: AuditRecord) => {
    const card = CARDS.find((c) => c.id === audit.card_id);
    if (card) navigate(card.route, { state: { shortcutId: audit.shortcut_id } });
  };

  const handleDeleteAudit = async (auditId: string) => {
    const { error } = await supabase.from("card_audit_results").delete().eq("id", auditId);
    if (!error) {
      setAudits((prev) => prev.filter((a) => a.id !== auditId));
      if (selectedAudit?.id === auditId) {
        setSelectedAudit(null);
        setViewMode("folder");
      }
    }
  };

  const handleExportAudit = (audit: AuditRecord) => {
    const data = buildFullExportData(audit.structured_data, audit.structured_data.requestedAuditType);
    setExportDialog({ open: true, title: `${shortcutLabel(audit.shortcut_id)} — ${audit.domain}`, data, auditResult: audit.structured_data, domain: audit.domain });
  };

  const cardMeta = activeCard ? CARDS.find((c) => c.id === activeCard) : null;

  const filteredAudits = audits.filter((a) =>
    !searchQuery || a.domain.toLowerCase().includes(searchQuery.toLowerCase()) || shortcutLabel(a.shortcut_id).toLowerCase().includes(searchQuery.toLowerCase())
  );
  const filteredChats = chats.filter((c) =>
    !searchQuery || (c.domain ?? "").toLowerCase().includes(searchQuery.toLowerCase()) || c.tab_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ── Render Functions ──

  const renderCardGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[18px]">
      {CARDS.map((card) => {
        const Icon = card.icon;
        const counts = cardCounts[card.id] || { audits: 0, chats: 0, artifacts: 0 };
        const total = counts.audits + counts.chats + counts.artifacts;
        return (
          <button key={card.id} onClick={() => handleCardClick(card.id)}
            className="group relative rounded-2xl border border-white/[0.07] bg-og01-card p-5 text-left transition-all duration-200 hover:-translate-y-[3px] hover:bg-og01-card-hover"
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = `color-mix(in srgb, ${card.color} 32%, transparent)`; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; }}>
            <ChevronRight className="absolute top-[22px] right-5 w-[17px] h-[17px] opacity-0 transition-all group-hover:opacity-100 group-hover:translate-x-0.5" style={{ color: card.color }} />
            <div className="flex items-center gap-3 mb-[18px]">
              <div className="grid h-11 w-11 flex-none place-items-center rounded-[11px]" style={{ background: `color-mix(in srgb, ${card.color} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${card.color} 26%, transparent)` }}>
                <Icon className="w-[21px] h-[21px]" style={{ color: card.color }} />
              </div>
              <div>
                <div className="text-[15.5px] font-semibold text-og01-text">{card.label}</div>
                <div className="mt-px text-[12.5px] text-og01-text-muted">{total} items</div>
              </div>
            </div>
            <div className="flex gap-3.5 border-t border-white/[0.07] pt-3.5 text-xs text-og01-text-secondary">
              <span className="inline-flex items-center gap-1.5"><FileText className="w-[13px] h-[13px] text-og01-text-muted" />{counts.audits} audits</span>
              <span className="inline-flex items-center gap-1.5"><MessageSquare className="w-[13px] h-[13px] text-og01-text-muted" />{counts.chats} chats</span>
              {counts.artifacts > 0 && <span className="inline-flex items-center gap-1.5"><Code className="w-[13px] h-[13px] text-og01-text-muted" />{counts.artifacts} artifacts</span>}
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderFolderView = () => {
    if (!cardMeta) return null;
    const Icon = cardMeta.icon;
    const accent = cardMeta.color;
    return (
      <div className="space-y-4" style={{ ["--accent" as string]: accent } as React.CSSProperties}>
        {/* Card header */}
        <div className="flex items-center gap-4">
          <div className="grid h-[54px] w-[54px] flex-none place-items-center rounded-[14px]" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)`, border: `1px solid color-mix(in srgb, ${accent} 26%, transparent)` }}>
            <Icon className="w-[26px] h-[26px]" style={{ color: accent }} />
          </div>
          <div>
            <h2 className="font-heading text-[26px] leading-[1.1] text-og01-text">{cardMeta.label}</h2>
            <div className="mt-0.5 text-[13px] text-og01-text-muted">{audits.length} audits · {chats.length} conversations</div>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className="flex w-fit gap-1.5 rounded-[13px] border border-white/[0.07] bg-og01-card p-1.5">
          {([
            { id: "audits" as SubTab, label: "Audits", icon: FileText, count: audits.length },
            { id: "chats" as SubTab, label: "Conversations", icon: MessageSquare, count: chats.length },
            { id: "artifacts" as SubTab, label: "Artifacts", icon: Code, count: artifacts.length },
          ]).map((tab) => {
            const TabIcon = tab.icon;
            const isActive = activeSubTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveSubTab(tab.id)}
                className="inline-flex items-center gap-2.5 rounded-[9px] px-4 py-2.5 text-[13.5px] font-medium transition-colors hover:text-og01-text"
                style={isActive
                  ? { color: accent, background: `color-mix(in srgb, ${accent} 16%, #0F0F18)`, border: `1px solid color-mix(in srgb, ${accent} 36%, transparent)` }
                  : { color: "#ABABC8", border: "1px solid transparent" }}>
                <TabIcon className="w-[15px] h-[15px]" />{tab.label}
                <span className="rounded-full px-2 py-0.5 text-[11px] font-bold" style={isActive ? { background: `color-mix(in srgb, ${accent} 22%, transparent)`, color: accent } : { background: "rgba(255,255,255,.07)", color: "#ABABC8" }}>{tab.count}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="flex items-center gap-2.5 rounded-[10px] border border-white/[0.07] bg-og01-card px-3.5 py-2.5">
          <Search className="w-4 h-4 text-og01-text-muted" />
          <input type="text" placeholder="Search by domain, audit type…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 bg-transparent text-[13.5px] text-og01-text outline-none placeholder:text-og01-text-muted" />
        </div>

        {/* Content */}
        {activeSubTab === "audits" && (
          filteredAudits.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {filteredAudits.map((audit) => (
                <button key={audit.id} onClick={() => handleAuditClick(audit)}
                  className="flex w-full items-center gap-3.5 rounded-[13px] border border-white/[0.07] bg-og01-card px-[18px] py-[15px] text-left transition-colors hover:border-white/[0.12] hover:bg-og01-card-hover">
                  <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px]" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
                    <BarChart3 className="w-[18px] h-[18px]" style={{ color: accent }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-og01-text">{shortcutLabel(audit.shortcut_id)}</div>
                    <div className="mt-0.5 text-[12.5px] text-og01-text-muted">{audit.domain} · {audit.region?.toUpperCase()}</div>
                  </div>
                  <div className="flex flex-none items-center gap-3.5">
                    <span className="text-[12.5px] text-og01-text-muted">{formatTimeAgo(audit.created_at)}</span>
                    <ChevronRight className="w-4 h-4 text-og01-text-muted" />
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-[54px] text-center text-og01-text-muted">
              <div className="mb-4 grid h-14 w-14 place-items-center rounded-[14px]" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)` }}>
                <FileText className="w-[26px] h-[26px]" style={{ color: accent }} />
              </div>
              <div className="mb-1 text-[15px] font-semibold text-og01-text">No saved audits yet</div>
              <div className="text-xs">Run an audit on this Smart Card and it'll be saved here.</div>
            </div>
          )
        )}

        {activeSubTab === "chats" && (
          filteredChats.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {filteredChats.map((chat) => {
                const msgCount = chat.messages?.length ?? 0;
                return (
                  <div key={chat.id} className="flex items-center gap-3.5 rounded-[13px] border border-white/[0.07] bg-og01-card px-[18px] py-[15px]">
                    <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px]" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
                      <MessageSquare className="w-[18px] h-[18px]" style={{ color: accent }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold text-og01-text">{chat.tab_id.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} conversation</div>
                      <div className="mt-0.5 text-[12.5px] text-og01-text-muted">{chat.domain || "—"} · {msgCount} messages</div>
                    </div>
                    <div className="flex flex-none items-center gap-3.5">
                      <span className="rounded-full px-2.5 py-[3px] text-[11px] font-semibold" style={chat.is_active ? { background: "color-mix(in srgb, #22C55E 14%, transparent)", color: "#22C55E" } : { background: "rgba(255,255,255,.05)", color: "#8080A0" }}>
                        {chat.is_active ? "Active" : "Archived"}
                      </span>
                      <span className="text-[12.5px] text-og01-text-muted">{formatTimeAgo(chat.updated_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center py-[54px] text-center text-og01-text-muted">
              <div className="mb-4 grid h-14 w-14 place-items-center rounded-[14px]" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)` }}>
                <MessageSquare className="w-[26px] h-[26px]" style={{ color: accent }} />
              </div>
              <div className="mb-1 text-[15px] font-semibold text-og01-text">No conversations yet</div>
              <div className="text-xs">Your chats with Ruby on this card will appear here.</div>
            </div>
          )
        )}

        {activeSubTab === "artifacts" && (
          artifacts.length > 0 ? (
            <div className="flex flex-col gap-2.5">
              {artifacts.map((art) => (
                <div key={art.id} className="flex items-center gap-3.5 rounded-[13px] border border-white/[0.07] bg-og01-card px-[18px] py-[15px]">
                  <div className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px]" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
                    <Code className="w-[18px] h-[18px]" style={{ color: accent }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-og01-text">{art.title}</div>
                    <div className="mt-0.5 text-[12.5px] text-og01-text-muted">
                      {art.domain} · {art.type} · {art.language}
                      {art.targetUrl && <span> · {art.targetUrl.replace(/^https?:\/\//, "")}</span>}
                    </div>
                  </div>
                  <div className="flex flex-none items-center gap-2.5">
                    <div className="flex gap-1.5">
                      <button onClick={() => { navigator.clipboard.writeText(art.code); }}
                        className="grid h-[30px] w-[30px] place-items-center rounded-lg border border-white/[0.07] bg-[#0F0F18] text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text" title="Copy code">
                        <Download className="w-[15px] h-[15px]" />
                      </button>
                      <button onClick={async () => { if (art.id) { await deleteArtifact(art.id); setArtifacts((prev) => prev.filter((a) => a.id !== art.id)); } }}
                        className="grid h-[30px] w-[30px] place-items-center rounded-lg border border-white/[0.07] bg-[#0F0F18] transition-colors hover:border-red-500/40" style={{ color: "#EF4444" }} title="Delete">
                        <Trash2 className="w-[15px] h-[15px]" />
                      </button>
                    </div>
                    <span className="text-[12.5px] text-og01-text-muted">{art.created_at ? formatTimeAgo(art.created_at) : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-[54px] text-center text-og01-text-muted">
              <div className="mb-4 grid h-14 w-14 place-items-center rounded-[14px]" style={{ background: `color-mix(in srgb, ${accent} 12%, transparent)` }}>
                <Code className="w-[26px] h-[26px]" style={{ color: accent }} />
              </div>
              <div className="mb-1 text-[15px] font-semibold text-og01-text">No artifacts yet</div>
              <div className="text-xs">Fixes Ruby generates — schema, meta, code — get saved here.</div>
            </div>
          )
        )}
      </div>
    );
  };

  const renderDetailView = () => {
    if (!selectedAudit) return null;
    const analysis = selectedAudit.analysis_text || selectedAudit.structured_data?.analysis || "";
    const recs = selectedAudit.recommendations || selectedAudit.structured_data?.recommendations || [];
    const accent = cardMeta?.color || "#8B5CF6";

    return (
      <div className="space-y-4" style={{ ["--accent" as string]: accent } as React.CSSProperties}>
        {/* Detail header */}
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h2 className="font-heading text-[28px] leading-[1.1] text-og01-text">{shortcutLabel(selectedAudit.shortcut_id)}</h2>
            <div className="mt-1.5 flex flex-wrap items-center gap-4 text-[12.5px] text-og01-text-muted">
              <span className="inline-flex items-center gap-1.5">{selectedAudit.domain}</span>
              <span>{selectedAudit.region?.toUpperCase()}</span>
              <span className="inline-flex items-center gap-1.5"><Clock className="w-[13px] h-[13px]" />{formatTimeAgo(selectedAudit.created_at)}</span>
              <span>Processed in {((selectedAudit.processing_time || 0) / 1000).toFixed(1)}s</span>
            </div>
          </div>
          <div className="flex flex-none items-center gap-2.5">
            <button onClick={() => handleExportAudit(selectedAudit)}
              className="inline-flex items-center gap-2 rounded-[10px] border border-white/[0.07] bg-og01-card px-4 py-2.5 text-[13.5px] font-semibold text-og01-text transition-colors hover:border-white/[0.12]">
              <Download className="w-[15px] h-[15px]" />Export
            </button>
            <button onClick={() => handleLoadToDashboard(selectedAudit)}
              className="inline-flex items-center gap-2 rounded-[10px] border-none px-4 py-2.5 text-[13.5px] font-semibold text-white transition-all hover:brightness-110"
              style={{ background: "linear-gradient(98deg,#8B5CF6 0%,#B16CEA 46%,#FF7A18 100%)", boxShadow: "0 8px 26px -12px rgba(255,122,24,.6)" }}>
              <ExternalLink className="w-[15px] h-[15px]" />Open in Dashboard
            </button>
            <button onClick={() => handleDeleteAudit(selectedAudit.id)}
              className="grid h-[38px] w-[38px] flex-none place-items-center rounded-[10px] border border-white/[0.07] bg-og01-card transition-colors hover:border-red-500/40"
              style={{ color: "#EF4444" }} aria-label="Delete">
              <Trash2 className="w-[18px] h-[18px]" />
            </button>
          </div>
        </div>

        {/* Analysis */}
        {analysis && (
          <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card">
            <div className="flex items-center gap-3 border-b border-white/[0.07] px-5 py-4">
              <div className="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
                <FileText className="w-4 h-4" style={{ color: accent }} />
              </div>
              <h3 className="font-heading text-[18px] text-og01-text">Detailed Analysis</h3>
            </div>
            <div className="whitespace-pre-wrap px-5 py-[18px] text-[13.5px] leading-relaxed text-og01-text-secondary">
              {analysis.slice(0, 3000)}{analysis.length > 3000 ? "..." : ""}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {recs.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card">
            <div className="flex items-center gap-3 border-b border-white/[0.07] px-5 py-4">
              <div className="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
                <BarChart3 className="w-4 h-4" style={{ color: accent }} />
              </div>
              <h3 className="font-heading text-[18px] text-og01-text">Recommendations</h3>
            </div>
            <div className="flex flex-col gap-3 px-5 py-[18px]">
              {recs.map((rec: any, i: number) => (
                <div key={i} className="flex items-start gap-3 text-[13.5px] leading-relaxed text-og01-text-secondary">
                  <span className="grid h-6 w-6 flex-none place-items-center rounded-[7px] text-[12px] font-semibold" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)`, color: accent, fontFamily: "'Geist Mono', ui-monospace, monospace" }}>{i + 1}</span>
                  <span>{typeof rec === "string" ? rec : rec.text || JSON.stringify(rec)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Audit Metadata */}
        <div className="overflow-hidden rounded-2xl border border-white/[0.07] bg-og01-card">
          <div className="flex items-center gap-3 border-b border-white/[0.07] px-5 py-4">
            <div className="grid h-[30px] w-[30px] flex-none place-items-center rounded-lg" style={{ background: `color-mix(in srgb, ${accent} 14%, transparent)` }}>
              <BarChart3 className="w-4 h-4" style={{ color: accent }} />
            </div>
            <h3 className="font-heading text-[18px] text-og01-text">Audit Metadata</h3>
          </div>
          <div className="px-5 py-[18px]">
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {[
                { label: "Source", value: selectedAudit.shortcut_id === "authority44-import" ? "OG01.ai" : "Ruby", mono: false },
                { label: "Report Type", value: shortcutLabel(selectedAudit.shortcut_id), mono: false },
                { label: "Region", value: selectedAudit.region?.toUpperCase() || "—", mono: false },
                { label: "Processing Time", value: `${((selectedAudit.processing_time || 0) / 1000).toFixed(1)}s`, mono: false },
              ].map((item, i) => (
                <div key={i} className="rounded-xl border border-white/[0.07] bg-[#0F0F18] px-4 py-[15px]">
                  <div className="mb-2 text-[11px] uppercase tracking-[0.06em] text-og01-text-muted">{item.label}</div>
                  <div className="break-words text-[14.5px] font-semibold text-og01-text-secondary" style={item.mono ? { fontFamily: "'Geist Mono', ui-monospace, monospace", fontSize: "13px" } : undefined}>{item.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ── Main Render ──

  const breadcrumb = (() => {
    const parts = [{ label: "Resource Vault", onClick: () => { setViewMode("cards"); setActiveCard(null); setSelectedAudit(null); setSearchParams({}); loadOverviewCounts(); } }];
    if (cardMeta && viewMode !== "cards") parts.push({ label: cardMeta.label, onClick: () => { setViewMode("folder"); setSelectedAudit(null); } });
    if (selectedAudit && viewMode === "detail") parts.push({ label: shortcutLabel(selectedAudit.shortcut_id), onClick: () => {} });
    return parts;
  })();

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden" style={{ background: "#08080E" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0, background: "radial-gradient(620px 420px at 88% -6%, rgba(139,92,246,.15), transparent 70%), radial-gradient(560px 460px at 102% 108%, rgba(255,122,24,.10), transparent 70%)" }} />
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.4, zIndex: 0, backgroundImage: "linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.025) 1px, transparent 1px)", backgroundSize: "46px 46px", WebkitMaskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)", maskImage: "radial-gradient(circle at 50% 20%, #000 30%, transparent 80%)" }} />

      <div className="relative z-[1] px-6 md:px-10 lg:px-14 py-6 mx-auto" style={{ maxWidth: "1480px" }}>
        {/* Header */}
        <div className="flex items-center gap-3.5 mb-1.5">
          {viewMode !== "cards" && (
            <button onClick={handleBack} aria-label="Back" className="grid place-items-center w-[38px] h-[38px] flex-none rounded-[10px] border border-white/[0.07] bg-og01-card text-og01-text-secondary transition-colors hover:border-white/[0.12] hover:text-og01-text">
              <ArrowLeft className="w-[18px] h-[18px]" />
            </button>
          )}
          <h1 className="flex items-center gap-3 font-heading text-[30px] leading-none text-og01-text">
            <FolderOpen className="w-[26px] h-[26px]" style={{ color: "#8B5CF6" }} />
            Resource Vault
          </h1>
          <div className="flex-1" />
          {viewMode === "cards" && (
            <div className="flex w-[300px] items-center gap-2.5 rounded-[10px] border border-white/[0.07] bg-og01-card px-3.5 py-2.5">
              <Search className="w-4 h-4 text-og01-text-muted" />
              <input type="text" placeholder="Search all resources…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1 bg-transparent text-[13.5px] text-og01-text outline-none placeholder:text-og01-text-muted" />
            </div>
          )}
        </div>

        {/* Breadcrumb */}
        {viewMode !== "cards" && (
          <div className="flex items-center gap-2.5 mb-5 pl-[52px] text-[13px] text-og01-text-muted">
            {breadcrumb.map((crumb, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ChevronRight className="w-[13px] h-[13px] opacity-60" />}
                <button onClick={crumb.onClick} className={i === breadcrumb.length - 1 ? "text-og01-text" : "transition-colors hover:text-og01-text-secondary"}>
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: "#8B5CF6" }} />
            <span className="ml-2 text-sm text-og01-text-muted">Loading…</span>
          </div>
        )}

        {/* Content */}
        {!loading && viewMode === "cards" && renderCardGrid()}
        {!loading && viewMode === "folder" && renderFolderView()}
        {!loading && viewMode === "detail" && renderDetailView()}
      </div>

      <RubyExportDialog
        open={exportDialog.open}
        onClose={() => setExportDialog((p) => ({ ...p, open: false }))}
        title={exportDialog.title}
        tier="full"
        data={exportDialog.data}
        fileName={`OG01_${exportDialog.domain || "report"}`}
        auditResult={exportDialog.auditResult}
        domain={exportDialog.domain}
      />
    </div>
  );
};

export default ResourceVaultV2;
